// TrustTicket helpdesk client.
//
// We call our OWN backend proxy at /api/helpdesk/tickets instead of
// hitting the external helpdesk (helpdesk.mytrustlab.co.in) directly
// from the browser. Reason: the helpdesk doesn't send CORS headers
// for our portal origin, so the browser blocks every direct fetch.
// Our backend forwards the request server-to-server, where CORS
// doesn't apply, and surfaces the helpdesk's status + body verbatim
// so this client doesn't have to know it's been hop-routed.
//
// `customerId` is the signed-in user's `client_id` (the trustlab
// client identifier — partners, hospitals, and doctor accounts all
// carry it). The helpdesk treats this value as opaque and just
// groups tickets by it; CSD looks the customer up via the
// auto-filled `customerName` / `customerEmail` / `customerPhone`
// when they need the human-readable context.

import { API_BASE_URL } from '../constants/api';

const TICKET_API = `${API_BASE_URL}/helpdesk`;

// Shared response handler. The helpdesk returns `{ error: "..." }`
// on 4xx — surface its message so the form can render a useful hint
// (e.g. "title is required" tells the user exactly which field is
// missing). For everything else we synthesise a generic message off
// the HTTP status.
const handle = async (res) => {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
};

// POST /tickets  → { id, code, status, raisedAt }
// `title` is required; everything else is optional but `customerId`
// must be set by the caller (we don't read auth state here — keep
// the service pure so it's easy to test).
//
// `branch` and `department` default to the values CSD uses for
// customer-portal tickets ("Hyderabad TS" / "Customer Support"), so
// every ticket lands in the right helpdesk queue without us forcing
// the customer to pick from a dropdown. Callers can override either
// if a future surface needs different routing.
export const raiseTicket = ({
  customerId,
  customerName,
  customerEmail,
  customerPhone,
  title,
  description,
  branch     = 'Hyderabad TS',
  department = 'Customer Support',
}) => fetch(`${TICKET_API}/tickets`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    title,
    description,
    branch,
    department,
  }),
}).then(handle);

// GET /tickets?customerId=X&limit=N  → { items, total }
// Sorted newest-first by the helpdesk. `limit` defaults to 50 (max
// 100 server-side) — the customer rarely has more than a handful so
// we don't paginate the UI.
export const listMyTickets = (customerId, limit = 50) => {
  const qs = new URLSearchParams({ customerId, limit }).toString();
  return fetch(`${TICKET_API}/tickets?${qs}`).then(handle);
};

// GET /tickets/:id?customerId=X  → single ticket (same shape as a
// list item). 404 if the id doesn't exist OR if `customerId` doesn't
// own that ticket — we never expose a global lookup so customers
// can't read each other's tickets by guessing IDs.
export const getTicket = (id, customerId) => {
  const qs = new URLSearchParams({ customerId }).toString();
  return fetch(`${TICKET_API}/tickets/${encodeURIComponent(id)}?${qs}`).then(handle);
};
